// src/contexts/WishlistContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { db } from '../services/firebase';
import { collection, doc, setDoc, getDocs, deleteDoc, query, where } from 'firebase/firestore';

const WishlistContext = createContext();

export const WishlistProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [wishlist, setWishlist] = useState([]);

  // Fetch wishlist from Firestore when user logs in
  useEffect(() => {
    if (!currentUser) {
      setWishlist([]);
      return;
    }

    const fetchWishlist = async () => {
      try {
        const q = query(
          collection(db, 'wishlists'),
          where('userId', '==', currentUser.uid)
        );
        const snapshot = await getDocs(q);
        const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data().book }));
        setWishlist(items);
      } catch (err) {
        console.error('Error fetching wishlist:', err);
      }
    };

    fetchWishlist();
  }, [currentUser]);

  const addToWishlist = async (book) => {
    if (!currentUser) return;

    try {
      const docRef = doc(collection(db, 'wishlists'));
      await setDoc(docRef, {
        userId: currentUser.uid,
        book
      });
      setWishlist(prev => [...prev, { id: docRef.id, ...book }]);
    } catch (err) {
      console.error('Error adding to wishlist:', err);
    }
  };

  const removeFromWishlist = async (bookId) => {
    if (!currentUser) return;

    try {
      const q = query(
        collection(db, 'wishlists'),
        where('userId', '==', currentUser.uid)
      );
      const snapshot = await getDocs(q);

      const docToDelete = snapshot.docs.find(doc => doc.data().book.id === bookId);
      if (docToDelete) {
        await deleteDoc(doc(db, 'wishlists', docToDelete.id));
        setWishlist(prev => prev.filter(item => item.id !== bookId));
      }
    } catch (err) {
      console.error('Error removing from wishlist:', err);
    }
  };

  const isWishlisted = (bookId) => wishlist.some(item => item.id === bookId);

  return (
    <WishlistContext.Provider
      value={{ wishlist, addToWishlist, removeFromWishlist, isWishlisted }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => useContext(WishlistContext);
